/**
 * Domain Query Service
 * 
 * Application service for domain read operations following CQRS pattern.
 * Optimized for data retrieval and presentation.
 */

import { Domain } from '../../domain/entities/Domain';
import type { IDomainRepository } from '../../../meta-model-design/domain/ports/IDomainRepository';
import type { IBoundedContextRepository } from '../../../meta-model-design/domain/ports/IBoundedContextRepository';
import { DomainDto } from '../queries/GetDomainQuery';
import { IPaginatedResult } from '@swoft/core';

export interface DomainListOptions {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  includeArchived?: boolean;
}

// Legacy support for existing services
export interface DomainWithStats extends DomainDto {
  boundedContextCount?: number;
  aggregateCount?: number;
}

export interface BoundedContextDto {
  id: string;
  boundedContextId: string;
  domainId: string;
  name: string;
  description?: string;
  type: string;
  ubiquitousLanguage: Record<string, string>;
  domainServices: any[];
  aggregates: any[];
  valueObjects: any[];
  entities: any[];
  createdAt: Date;
  updatedAt: Date;
}

/**
 * New CQRS-based Domain Query Service
 * 
 * This service replaces the legacy DomainQueryService with proper CQRS patterns.
 * Provides read operations for domains and bounded contexts.
 */
export class DomainCQRSQueryService {
  constructor(
    private domainRepository: IDomainRepository,
    private boundedContextRepository: IBoundedContextRepository
  ) {}

  async getDomain(domainId: string): Promise<DomainDto | null> {
    const domain = await this.domainRepository.findById(domainId);
    if (!domain) {
      return null;
    }

    return this.toDto(domain);
  }

  async listDomains(options: DomainListOptions = {}): Promise<IPaginatedResult<DomainDto>> {
    console.log('🔍 DEBUG: DomainCQRSQueryService.listDomains called with options:', options);
    const page = options.page || 1;
    const pageSize = options.pageSize || 10;

    console.log('🔍 DEBUG: Calling domainRepository.findAll with:', { page, pageSize, searchTerm: options.searchTerm, includeArchived: options.includeArchived });
    const result = await this.domainRepository.findAll({
      page,
      pageSize,
      searchTerm: options.searchTerm,
      includeArchived: options.includeArchived
    });

    console.log('🔍 DEBUG: Repository result:', { 
      domainsCount: result.domains.length, 
      totalCount: result.totalCount,
      firstDomain: result.domains[0] ? { id: result.domains[0].id, name: result.domains[0].name } : 'none'
    });

    const totalPages = Math.ceil(result.totalCount / pageSize);

    const domainDtos: DomainDto[] = result.domains.map(domain => this.toDto(domain));

    console.log('🔍 DEBUG: Mapped to DTOs:', { count: domainDtos.length });

    return {
      items: domainDtos,
      totalCount: result.totalCount,
      page,
      pageSize,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1
    };
  }

  async searchDomains(searchTerm: string): Promise<DomainDto[]> {
    const domains = await this.domainRepository.findByName(searchTerm);
    return domains ? [this.toDto(domains)] : [];
  }

  async getDomainsWithStats(includeStats: boolean = false): Promise<DomainWithStats[]> {
    const result = await this.domainRepository.findAll({});
    const domains = result.domains.map(domain => this.toDto(domain) as DomainWithStats);
    
    if (includeStats) {
      // Add bounded context and aggregate counts
      for (const domain of domains) {
        const boundedContextsResult = await this.boundedContextRepository.findByDomainId(domain.domainId);
        domain.boundedContextCount = boundedContextsResult.totalCount;
        domain.aggregateCount = boundedContextsResult.contexts.reduce((total: number, bc: any) => 
          total + (bc.aggregates?.length || 0), 0);
      }
    }
    
    return domains;
  }

  private toDto(domain: Domain): DomainDto {
    return {
      domainId: domain.id,
      name: domain.name,
      description: domain.description,
      vision: domain.vision,
      ubiquitousLanguage: domain.ubiquitousLanguage,
      createdAt: domain.createdAt,
      updatedAt: domain.updatedAt
    };
  }
}